import { supabase } from "@/integrations/supabase/client";
import { composerBus } from "@/lib/composerBus";
import { handleAiInvocationError } from "@/lib/aiErrors";
import type {
  ContentBriefDraft, ContentBriefPublication,
  RunBriefDraftInput, RunBriefDraftResult,
  RunBriefPublicationInput, RunBriefPublicationResult,
} from "../types";
import { parseBriefDraft, parseBriefPublication } from "../snapshot";

// Note: runBriefPlanningPass was removed in Phase B2c. Planning is now a
// multi-turn conversation owned by BriefIntakeChat / BriefIntakePanel and the
// streaming `content-plan-brief` edge function. There is no single-shot
// fallback by design.

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type SseEvent = { event: string; data: string };

// Minimal inline SSE parser. Each event ends with a blank line; we accept
// `event:` and `data:` lines (additional `data:` lines on the same event are
// concatenated with newlines per spec).
async function* readSseEvents(response: Response): AsyncGenerator<SseEvent> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let event = "message";
        const dataLines: string[] = [];
        for (const line of raw.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
        }
        yield { event, data: dataLines.join("\n") };
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export const runBriefDraftPass = async (
  workspaceId: string,
  input: RunBriefDraftInput & { selectedAvatarId?: string | null },
): Promise<RunBriefDraftResult> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Not authenticated");

  let response: Response;
  try {
    response = await fetch(`${SUPABASE_URL}/functions/v1/content-draft-brief`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        workspace_id: workspaceId,
        piece_id: input.briefId,
        revision_prompt: input.revisionPrompt?.trim() || null,
        plan_id: input.planId ?? null,
        selected_avatar_id: input.selectedAvatarId ?? null,
      }),
    });
  } catch (err) {
    throw err instanceof Error ? err : new Error("Network error");
  }

  // Non-SSE error responses (auth/validation/tier gates) come back as JSON.
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("text/event-stream")) {
    let message = `Draft request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch { /* ignore */ }
    const err = new Error(message);
    (err as { status?: number }).status = response.status;
    if (await handleAiInvocationError(err)) throw err;
    throw err;
  }

  composerBus.emit("brief-draft:stream", {
    phase: "start",
    briefId: input.briefId,
    label: input.revisionPrompt ? "Revising draft…" : "Drafting from brief…",
  });

  let lastMarkdown = "";
  let finalDraft: ContentBriefDraft | null = null;
  let streamError: { message: string; status?: number } | null = null;

  try {
    for await (const { event, data } of readSseEvents(response)) {
      if (!data) continue;
      let parsed: Record<string, unknown>;
      try { parsed = JSON.parse(data); } catch { continue; }

      if (event === "chunk") {
        const body = typeof parsed.draft_body === "string" ? parsed.draft_body : "";
        if (body && body !== lastMarkdown) {
          lastMarkdown = body;
          composerBus.emit("brief-draft:stream", {
            phase: "chunk",
            briefId: input.briefId,
            markdown: body,
          });
        }
      } else if (event === "done") {
        finalDraft = (parsed.draft ?? null) as ContentBriefDraft | null;
        if (finalDraft?.draft_body) lastMarkdown = finalDraft.draft_body;
      } else if (event === "error") {
        streamError = {
          message: typeof parsed.error === "string" ? parsed.error : "Draft generation failed",
          status: typeof parsed.status === "number" ? parsed.status : undefined,
        };
      }
    }
  } catch (err) {
    streamError = { message: err instanceof Error ? err.message : "Draft stream interrupted" };
  }

  composerBus.emit("brief-draft:stream", {
    phase: "end",
    briefId: input.briefId,
    markdown: lastMarkdown,
    aborted: Boolean(streamError),
  });

  if (streamError) {
    const err = new Error(streamError.message);
    if (streamError.status) (err as { status?: number }).status = streamError.status;
    if (await handleAiInvocationError(err)) throw err;
    throw err;
  }

  if (!finalDraft) throw new Error("Draft generation did not return a saved result.");
  return { draft: parseBriefDraft(finalDraft) } satisfies RunBriefDraftResult;
};

export const runBriefPublicationPrep = async (
  workspaceId: string,
  input: RunBriefPublicationInput,
): Promise<RunBriefPublicationResult> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Not authenticated");

  let response: Response;
  try {
    response = await fetch(`${SUPABASE_URL}/functions/v1/content-prepare-publication`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        workspace_id: workspaceId,
        piece_id: input.briefId,
        draft_id: input.draftId,
        destination_format: input.destinationFormat,
        revision_prompt: input.revisionPrompt?.trim() || null,
        publish_type_key: input.publishTypeKey ?? null,
      }),
    });
  } catch (err) {
    throw err instanceof Error ? err : new Error("Network error");
  }

  const contentType = response.headers.get("content-type") || "";

  // Skip-AI branch + auth/validation/tier short-circuits return JSON.
  if (!contentType.includes("text/event-stream")) {
    let body: { publication?: ContentBriefPublication; error?: string } = {};
    try { body = await response.json(); } catch { /* ignore */ }
    if (!response.ok || body.error) {
      const err = new Error(body.error ?? `Publication prep failed (${response.status})`);
      (err as { status?: number }).status = response.status;
      if (await handleAiInvocationError(err)) throw err;
      throw err;
    }
    if (!body.publication) throw new Error("Publication preparation did not return a saved result.");
    return { publication: parseBriefPublication(body.publication) } satisfies RunBriefPublicationResult;
  }

  composerBus.emit("publication:stream", {
    phase: "start",
    briefId: input.briefId,
    draftId: input.draftId,
    destinationFormat: input.destinationFormat,
    label: input.revisionPrompt ? "Re-preparing publication…" : "Preparing publication…",
  });

  let lastMarkdown = "";
  let finalPublication: ContentBriefPublication | null = null;
  let streamError: { message: string; status?: number } | null = null;

  try {
    for await (const { event, data } of readSseEvents(response)) {
      if (!data) continue;
      let parsed: Record<string, unknown>;
      try { parsed = JSON.parse(data); } catch { continue; }

      if (event === "chunk") {
        const body = typeof parsed.prepared_body === "string" ? parsed.prepared_body : "";
        if (body && body !== lastMarkdown) {
          lastMarkdown = body;
          composerBus.emit("publication:stream", {
            phase: "chunk",
            briefId: input.briefId,
            draftId: input.draftId,
            destinationFormat: input.destinationFormat,
            markdown: body,
          });
        }
      } else if (event === "done") {
        finalPublication = (parsed.publication ?? null) as ContentBriefPublication | null;
        if (typeof (finalPublication as { prepared_body?: string } | null)?.prepared_body === "string") {
          lastMarkdown = (finalPublication as { prepared_body?: string }).prepared_body ?? lastMarkdown;
        }
      } else if (event === "error") {
        streamError = {
          message: typeof parsed.error === "string" ? parsed.error : "Publication prep failed",
          status: typeof parsed.status === "number" ? parsed.status : undefined,
        };
      }
    }
  } catch (err) {
    streamError = { message: err instanceof Error ? err.message : "Publication stream interrupted" };
  }

  composerBus.emit("publication:stream", {
    phase: "end",
    briefId: input.briefId,
    draftId: input.draftId,
    destinationFormat: input.destinationFormat,
    markdown: lastMarkdown,
    aborted: Boolean(streamError),
  });

  if (streamError) {
    const err = new Error(streamError.message);
    if (streamError.status) (err as { status?: number }).status = streamError.status;
    if (await handleAiInvocationError(err)) throw err;
    throw err;
  }

  if (!finalPublication) throw new Error("Publication preparation did not return a saved result.");
  return { publication: parseBriefPublication(finalPublication) } satisfies RunBriefPublicationResult;
};
